import React, { FunctionComponent } from "react";
import { IDataColumnDefProps, ITextQuery, PickerInput } from "@plex/react-components";
import { SourceAPI } from "../NodeTypes/TypeDefinitions";
import { INodeConfigPropertiesProps } from "../NodeTypes/PropertyTypeDefinitions";
import "./PickerFormField.scss";
import { allEdgesDeletionMode, deleteEdgesFromNodes } from "../Util/EdgeUtil";
import { useReactFlow, useUpdateNodeInternals } from "reactflow";

export interface ISourceApiPickerProps extends INodeConfigPropertiesProps {
  dialogTitle: string;
  displayName: string;
  searchFunction?: (query: string) => Promise<SourceAPI[]>;
  multiSelect?: boolean;
  /** Optional override header for primary name column (defaults to 'Display Name'). */
  columnTitle?: string;
  /** Optional extra columns. */
  additionalColumns?: Array<{
    id: string;
    title: string;
    valueSelector: (row: SourceAPI) => string | undefined;
    searchable?: boolean;
  }>;
  /** Placement for additionalColumns relative to the primary (name) column. Defaults to 'after'. */
  additionalColumnsPlacement?: 'before' | 'after';
  /** Optional picker dialog width. Defaults to 900. */
  initialWidth?: number;
  /**
   * When true, existing connections into this node's input handles are preserved across a
   * selection change/clear instead of being deleted. Callers should set this for pickers whose
   * inputs are client-owned (declared statically on the node type, e.g. the Plex Agent node's
   * instructions/inputData) rather than driven by the picked value.
   */
  preserveInputConnections?: boolean;
}

export const SourceApiPickerFormField: FunctionComponent<ISourceApiPickerProps> = (props) => {
  let [{ pickerSelected, pickerData, searchText }, setPicker] = React.useState({
    pickerSelected: props.node?.data.nodeProperties[props.name]
      ? Array.isArray(props.node?.data.nodeProperties[props.name])
        ? props.node?.data.nodeProperties[props.name]
        .map((propId, index) => ({ id: propId, name: props.node?.data?.nodeProperties[props.displayName][index] } as SourceAPI))
        : [
            {
              id: props.node?.data.nodeProperties[props.name],
              name: props.node?.data.nodeProperties[props.displayName]
            } as SourceAPI
          ]
      : ([] as SourceAPI[]),
    pickerData: [] as { id: string; name: string }[],
    searchText: ""
  });
  const reactFlowInstance = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();
  const [, _setState] = React.useState({ value: props.node?.data.nodeProperties || {} });
  const [recordLimitExceededState, setRecordLimitExceededState] = React.useState(false);
  const [searchErrorState, setSearchErrorState] = React.useState("");

  const handlePickerSearch = (query: ITextQuery, cols: Array<IDataColumnDefProps<SourceAPI>>) => {
    if (props.searchFunction) {
      props
        .searchFunction(query?.displayText)
        .then((results: SourceAPI[]) => {
          setRecordLimitExceededState(false);
          setSearchErrorState("");

          let items: {
            id: string;
            name: string;
          }[];
          if (cols.length === 0) {
            items = results.filter((x) => (x.name || "").toLowerCase().includes(query.query.toLowerCase()));
          } else {
            items = results.filter((row: any) =>
              query.isMatch(cols.map((col) => (col.valueSelector ? String(col.valueSelector(row)) : "")))
            );
          }

          setPicker((x: any) => ({ ...x, pickerData: items }));
        })
        .catch((e) => {
          setSearchErrorState("An error occurred when searching.");
          setRecordLimitExceededState(false);
          console.error(e);
        });
    }
  };

  const handlePickerSearchTextChanged = (value: string) => {
    setPicker((x: any) => ({ ...x, searchText: value }));
  };

  return (
    <PickerInput<SourceAPI>
      key={props.dialogTitle + "Picker"}
      dialogTitle={props.dialogTitle}
      initialWidth={props.initialWidth ?? 900}
      disabled={false}
      selected={props.node?.data.nodeProperties[props.name]
        ? Array.isArray(props.node?.data.nodeProperties[props.name])
          ? props.node?.data.nodeProperties[props.name]
          .map((propId, index) => ({ id: propId, name: props.node?.data?.nodeProperties[props.displayName][index] } as SourceAPI))
          : [
              {
                id: props.node?.data.nodeProperties[props.name],
                name: props.node?.data.nodeProperties[props.displayName]
              } as SourceAPI
            ]
        : ([] as SourceAPI[])}
      onSelectionChanged={(values: any) => {
        const idsSelected = props.multiSelect === true
          ? values.map((v) => v?.id)
          : values[0]?.id;

        const displayNames = props.multiSelect === true
          ? values.map((v) => v?.name)
          : values[0]?.name;

        if (props.node!.data.nodeProperties[props.name] === idsSelected) {
          return;
        }

        ((props.beforeChange && props.beforeChange(values)) ?? Promise.resolve()).then(() => {
          deleteEdgesFromNodes([props.node!.id], reactFlowInstance, updateNodeInternals, {
            ...allEdgesDeletionMode,
            controlEdges: false,
            outputEdges: false,
            inputEdges: !props.preserveInputConnections
          });

          if (values.length === 0) {
            setPicker((x) => ({ ...x, pickerSelected: [], searchText: "" }));
            [];
            props.node!.data.nodeProperties[props.name] = null;
            props.node!.data.nodeProperties[props.displayName] = null;

            if (props.onChange) {
              props.onChange();
            }
            return;
          }

          if (values.length === 0) {
            pickerSelected = []
          } else if (props.multiSelect === true) {
            pickerSelected = values
          } else {
            pickerSelected = [values[0]]
          }

          setPicker((x) => ({
            ...x,
            pickerSelected: pickerSelected,
            searchText: ""
          }));
          props.node!.data.nodeProperties[props.name] = idsSelected;
          props.node!.data.nodeProperties[props.displayName] = displayNames;

          if (props.onChange) {
            props.onChange();
          }
        });
      }}
      keySelector={(row: SourceAPI) => row?.id}
      displaySelector={(row: SourceAPI) => row?.name}
      data={pickerData}
      onSearch={handlePickerSearch}
      searchDisabled={false}
      isOpen={false}
      multiSelect={props.multiSelect ?? false}
      maxDisplay={5}
      recordLimitExceeded={recordLimitExceededState}
      errorMessage={searchErrorState ? searchErrorState : undefined}
      searchText={searchText}
      onSearchTextChange={handlePickerSearchTextChanged}
    >
      {props.additionalColumnsPlacement === 'before' && props.additionalColumns?.map(col => (
        <PickerInput.Column<SourceAPI>
          key={col.id}
          id={col.id}
          title={col.title}
          valueSelector={(row: SourceAPI) => col.valueSelector(row)}
          searchable={col.searchable ?? true}
        >
          {(row: SourceAPI) => col.valueSelector(row)}
        </PickerInput.Column>
      ))}
      <PickerInput.Column<SourceAPI>
        key="1"
        id={props.displayName}
        title={props.columnTitle ?? "Name"}
        valueSelector={(row: SourceAPI) => row.name}
        searchable
      >
        {(row: SourceAPI) => row.name}
      </PickerInput.Column>
      {(props.additionalColumnsPlacement === undefined || props.additionalColumnsPlacement === 'after') && props.additionalColumns?.map(col => (
        <PickerInput.Column<SourceAPI>
          key={col.id}
          id={col.id}
          title={col.title}
          valueSelector={(row: SourceAPI) => col.valueSelector(row)}
          searchable={col.searchable ?? true}
        >
          {(row: SourceAPI) => col.valueSelector(row)}
        </PickerInput.Column>
      ))}
    </PickerInput>
  );
};
